/**
 * Deal Product Card - Modern, polished design with urgency indicators
 * Supports grouped products with expandable variants
 * Includes prize highlighting for early access products
 */

import { useState } from 'react';
import {
  MapPin,
  ExternalLink,
  ImageOff,
  Clock,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Package,
  Unlock,
} from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import type { DealProduct, GroupedDealProduct, MysteryType } from '../types';
import { formatPrice, getRelativeTime, isNew, isHot } from '../utils/helpers';
import { buildDirectAsIsUrl } from '../utils/url';

interface Props {
  product: DealProduct;
  group?: GroupedDealProduct;
  index?: number;
}

const CATEGORY_STYLES: Record<string, { border: string; text: string; bg: string; glow: string }> = {
  last_chance: { border: 'border-orange-300', text: 'text-orange-600', bg: 'bg-orange-50/90', glow: 'shadow-orange-100' },
  family_offers: { border: 'border-blue-300', text: 'text-blue-600', bg: 'bg-blue-50/90', glow: 'shadow-blue-100' },
  limited_time: { border: 'border-purple-300', text: 'text-purple-600', bg: 'bg-purple-50/90', glow: 'shadow-purple-100' },
  as_is: { border: 'border-emerald-300', text: 'text-emerald-600', bg: 'bg-emerald-50/90', glow: 'shadow-emerald-100' },
};

const CATEGORY_KEYS: Record<string, { key: string; fallback: string }> = {
  last_chance: { key: 'deals.category.lastChance', fallback: 'Last Chance' },
  family_offers: { key: 'deals.category.family', fallback: 'Family' },
  limited_time: { key: 'deals.category.limited', fallback: 'Limited' },
  as_is: { key: 'deals.category.asIs', fallback: 'As-Is' },
};

// Prize tier styling for early access products
const PRIZE_STYLES: Record<MysteryType, { border: string; glow: string; badge: string; badgeBg: string }> = {
  super: {
    border: 'border-amber-400',
    glow: 'shadow-lg shadow-amber-200/50 ring-2 ring-amber-400/30',
    badge: 'text-amber-600',
    badgeBg: 'bg-amber-50',
  },
  fresh: {
    border: 'border-purple-400',
    glow: 'shadow-lg shadow-purple-200/50 ring-2 ring-purple-400/30',
    badge: 'text-purple-600',
    badgeBg: 'bg-purple-50',
  },
  standard: {
    border: 'border-blue-400',
    glow: 'shadow-lg shadow-blue-200/50 ring-2 ring-blue-400/30',
    badge: 'text-blue-600',
    badgeBg: 'bg-blue-50',
  },
};

function getProductUrl(product: DealProduct): string {
  return buildDirectAsIsUrl(product.source_url, product.product_code, product.country);
}

// Variant card shown in expanded view
function VariantCard({ product }: { product: DealProduct }) {
  const { t } = useLanguage();
  const productUrl = getProductUrl(product);
  const savings = product.original_price && product.price
    ? product.original_price - product.price
    : null;

  return (
    <a
      href={productUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors group/variant"
    >
      {/* Mini Image */}
      <div className="w-16 h-16 flex-shrink-0 bg-white rounded-lg overflow-hidden">
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={product.name || product.product_code}
            className="w-full h-full object-contain p-1"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageOff className="w-6 h-6 text-gray-200" />
          </div>
        )}
      </div>

      {/* Details */}
      <div className="flex-1 min-w-0">
        {/* Price */}
        <div className="flex items-baseline gap-2">
          <span className="font-bold text-gray-900">{formatPrice(product.price)}</span>
          {product.original_price && product.original_price > (product.price || 0) && (
            <span className="text-xs text-gray-400 line-through">{formatPrice(product.original_price)}</span>
          )}
          {savings && savings > 0 && (
            <span className="text-xs text-green-600 font-medium">{t('deals.save', 'Save')} {formatPrice(savings)}</span>
          )}
        </div>

        {/* Condition */}
        {product.as_is_condition && (
          <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{product.as_is_condition}</p>
        )}

        {/* Store */}
        <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
          <MapPin className="w-3 h-3" />
          <span className="truncate">{product.store_name}</span>
        </div>
      </div>

      {/* External Link */}
      <ExternalLink className="w-4 h-4 text-gray-300 group-hover/variant:text-primary-500 transition-colors flex-shrink-0" />
    </a>
  );
}

export default function DealsProductCard({ product, group, index = 0 }: Props) {
  const { t } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);
  const hasMultipleVariants = group && group.totalCount > 1;

  const categoryStyle = CATEGORY_STYLES[product.category] || CATEGORY_STYLES.last_chance;
  const categoryKey = CATEGORY_KEYS[product.category] || CATEGORY_KEYS.last_chance;
  const productUrl = getProductUrl(product);
  const relativeTime = getRelativeTime(product.first_seen);
  const isNewItem = isNew(product.first_seen);
  const isHotDeal = isHot(product.discount_percent);

  // Prize highlighting for early access products
  const isPrize = product.is_prize === true && !product.is_mystery;
  const prizeType = product.mystery_type;
  const prizeStyle = isPrize && prizeType ? PRIZE_STYLES[prizeType] : null;

  const savings = product.original_price && product.price
    ? product.original_price - product.price
    : null;

  // Handle click - expand if multiple variants, otherwise navigate
  const handleClick = (e: React.MouseEvent) => {
    if (hasMultipleVariants) {
      e.preventDefault();
      setIsExpanded(!isExpanded);
    }
  };

  // Build card classes
  const baseClasses = 'group block bg-white rounded-2xl overflow-hidden transition-all duration-300 ease-out hover:-translate-y-1.5';
  const shadowClasses = isPrize && prizeStyle
    ? prizeStyle.glow
    : 'shadow-sm hover:shadow-2xl';
  const borderClasses = isPrize && prizeStyle
    ? `border-2 ${prizeStyle.border}`
    : 'border border-gray-100 hover:border-gray-200';
  const expandedClasses = isExpanded ? 'ring-2 ring-primary-500 ring-offset-2' : '';
  const cursorClasses = hasMultipleVariants ? 'cursor-pointer' : '';

  const cardClasses = `${baseClasses} ${shadowClasses} ${borderClasses} ${expandedClasses} ${cursorClasses}`;

  // Card wrapper - use button for expandable, link for single
  const CardWrapper = hasMultipleVariants ? 'div' : 'a';
  const cardProps = hasMultipleVariants
    ? {
        onClick: handleClick,
        className: cardClasses,
        style: { animationDelay: `${index * 50}ms` },
      }
    : {
        href: productUrl,
        target: '_blank',
        rel: 'noopener noreferrer',
        className: cardClasses,
        style: { animationDelay: `${index * 50}ms` },
      };

  return (
    <div>
      <CardWrapper {...(cardProps as React.HTMLAttributes<HTMLElement> & { href?: string; target?: string; rel?: string })}>
        {/* Image Container */}
        <div className="relative aspect-square bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
          {product.image_url ? (
            <img
              src={product.image_url}
              alt={product.name || product.product_code}
              className="w-full h-full object-contain p-6 transition-transform duration-500 ease-out group-hover:scale-110"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageOff className="w-16 h-16 text-gray-200" />
            </div>
          )}

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          {/* Category Badge - Top Left */}
          <div
            className={`absolute top-3 left-3 px-2.5 py-1 text-xs font-semibold rounded-full border backdrop-blur-md shadow-sm ${categoryStyle.bg} ${categoryStyle.border} ${categoryStyle.text}`}
          >
            {t(categoryKey.key, categoryKey.fallback)}
          </div>

          {/* Prize Badge - Top Left (below category) */}
          {isPrize && prizeStyle && (
            <div
              className={`absolute top-12 left-3 flex items-center gap-1 px-2 py-1 text-xs font-bold rounded-full ${prizeStyle.badgeBg} ${prizeStyle.badge}`}
            >
              <Unlock className="w-3 h-3" />
              {t('deals.badge.earlyAccess', 'Early Access')}
            </div>
          )}

          {/* Quantity Badge - Top Right (for grouped products) */}
          {hasMultipleVariants && (
            <div className="absolute top-3 right-3 flex items-center gap-1 px-2.5 py-1.5 bg-gray-900/90 text-white text-xs font-bold rounded-full shadow-lg backdrop-blur-sm">
              <Package className="w-3.5 h-3.5" />
              {group.totalCount} {t('deals.available', 'available')}
            </div>
          )}

          {/* New/Hot Badge - Top Right (only show if no quantity badge) */}
          {!hasMultipleVariants && (isNewItem || isHotDeal) && (
            <div className="absolute top-3 right-3 flex items-center gap-1">
              {isNewItem && (
                <div className="flex items-center gap-1 px-2 py-1 bg-primary-500 text-white text-xs font-bold rounded-full shadow-lg animate-pulse">
                  <span className="w-1.5 h-1.5 bg-white rounded-full" />
                  {t('deals.badge.new', 'NEW')}
                </div>
              )}
              {isHotDeal && !isNewItem && (
                <div className="flex items-center gap-1 px-2 py-1 bg-amber-500 text-white text-xs font-bold rounded-full shadow-lg">
                  <Sparkles className="w-3 h-3" />
                  {t('deals.badge.hot', 'HOT')}
                </div>
              )}
            </div>
          )}

          {/* Discount Badge - Bottom Right */}
          {product.discount_percent && product.discount_percent > 0 && (
            <div className="absolute bottom-3 right-3 px-3 py-1.5 bg-red-500 text-white text-sm font-bold rounded-full shadow-xl">
              -{product.discount_percent}%
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Product Name */}
          <h3 className="font-bold text-gray-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors duration-200 text-base">
            {product.name || t('deals.unknownProduct', 'Unknown Product')}
          </h3>

          {/* Product Code with External Link (or Expand hint) */}
          <p className="text-xs text-gray-400 mt-1 flex items-center gap-1.5">
            <span className="font-mono">{product.product_code}</span>
            {hasMultipleVariants ? (
              <span className="flex items-center gap-0.5 text-primary-500">
                {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {isExpanded ? t('deals.collapse', 'Collapse') : t('deals.seeAll', 'See all')}
              </span>
            ) : (
              <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
            )}
          </p>

          {/* Price Section */}
          <div className="mt-3">
            <div className="flex items-baseline gap-2 flex-wrap">
              {hasMultipleVariants && group.priceRange.min !== group.priceRange.max ? (
                // Price range for grouped products
                <span className="text-2xl font-bold text-gray-900">
                  {formatPrice(group.priceRange.min)} - {formatPrice(group.priceRange.max)}
                </span>
              ) : product.price !== null ? (
                <>
                  <span className="text-2xl font-bold text-gray-900">
                    {formatPrice(product.price)}
                  </span>
                  {product.original_price && product.original_price > product.price && (
                    <span className="text-sm text-gray-400 line-through">
                      {formatPrice(product.original_price)}
                    </span>
                  )}
                </>
              ) : (
                <span className="text-sm text-gray-400">{t('deals.priceNotAvailable', 'Price not available')}</span>
              )}
            </div>

            {/* Savings Chip (only for single products) */}
            {!hasMultipleVariants && savings && savings > 0 && (
              <div className="mt-2 inline-flex items-center px-2 py-0.5 bg-green-50 border border-green-200 text-green-700 text-xs font-semibold rounded-full">
                {t('deals.save', 'Save')} {formatPrice(savings)}
              </div>
            )}
          </div>

          {/* As-Is Condition (only for single products) */}
          {!hasMultipleVariants && product.as_is_condition && (
            <p className="mt-2 text-xs text-gray-500 line-clamp-1 italic bg-gray-50 px-2 py-1 rounded">
              {product.as_is_condition}
            </p>
          )}

          {/* Footer */}
          <div className="mt-4 pt-3 border-t border-gray-100 space-y-1.5">
            {/* Store Location (or multiple stores hint) */}
            <div className="flex items-center gap-1.5 text-xs text-gray-600">
              <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              {hasMultipleVariants ? (
                <span className="truncate font-medium">
                  {new Set(group.variants.map(v => v.store_id)).size} {new Set(group.variants.map(v => v.store_id)).size > 1 ? t('deals.stores', 'stores') : t('deals.store', 'store')}
                </span>
              ) : (
                <span className="truncate font-medium">
                  {product.store_name}
                  {product.store_city && `, ${product.store_city}`}
                </span>
              )}
            </div>

            {/* Time Info */}
            {relativeTime && (
              <div className="flex items-center gap-1.5 text-xs text-gray-400">
                <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                <span>{t('deals.added', 'Added')} {relativeTime}</span>
              </div>
            )}
          </div>
        </div>
      </CardWrapper>

      {/* Expanded Variants */}
      {hasMultipleVariants && isExpanded && (
        <div className="mt-2 p-3 bg-white rounded-xl border border-gray-200 shadow-sm space-y-2 animate-in slide-in-from-top-2 duration-200">
          <div className="text-xs font-medium text-gray-500 px-1 mb-2">
            {t('deals.itemsAvailable', '{count} items available:').replace('{count}', String(group.totalCount))}
          </div>
          {group.variants.map((variant, idx) => (
            <VariantCard key={`${variant.product_code}-${variant.store_id}-${idx}`} product={variant} />
          ))}
        </div>
      )}
    </div>
  );
}
